/**
 * The artifacts rail's layout — renders in one column, or the older list of rows — remembered per
 * browser.
 *
 * A preference about how much of the pane one artifact gets, not a fact about the deck, so it lives
 * in localStorage and never in the blob: two people on one deck may want it two ways, and a deck
 * opened on a phone should not inherit the layout a desktop chose. Read after mount rather than in
 * the initial state so the server and the first client paint agree; the one-frame swap to a saved
 * `list` costs less than a hydration mismatch on every load.
 */
import { useCallback, useEffect, useState } from 'react';
import type { ArtifactLayout } from '~/design-system/components/v1_5/Directory/theme';

const KEY = 'deck:directory:artifact-layout';

function read(): ArtifactLayout {
  try {
    return window.localStorage.getItem(KEY) === 'list' ? 'list' : 'grid';
  } catch {
    return 'grid';
  }
}

export function useArtifactLayout(): [ArtifactLayout, (next: ArtifactLayout) => void] {
  const [layout, setLayout] = useState<ArtifactLayout>('grid');
  useEffect(() => {
    setLayout(read());
  }, []);
  const set = useCallback((next: ArtifactLayout) => {
    setLayout(next);
    try {
      window.localStorage.setItem(KEY, next);
    } catch {
      // Storage refused (private mode, quota): the choice still holds for this visit.
    }
  }, []);
  return [layout, set];
}
